import { LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { ThunkDispatch } from 'redux-thunk';
import { Action as ReduxAction, AnyAction as ReduxAnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import Immutable from '../../../utils/immutable-type';
import { requestApi } from '../../../services/api-new';
import { getFutureTimestamp } from './product-switch-utils';

// Constants
const REDUCER_NAME = 'productSwitch';
const COOLOFF_API_BASE_URL = '/cooloff/client';
const ACTION_PREFIX = '@komigen/account/product-switch/';

// Actions
const PRODUCT_COOLOFF_CLEARED = `${ACTION_PREFIX}PRODUCT_COOLOFF_CLEARED`;
const PRODUCT_COOLOFF_UPDATED = `${ACTION_PREFIX}PRODUCT_COOLOFF_UPDATED`;
const UPDATED_MODAL_OPEN = `${ACTION_PREFIX}UPDATED_MODAL_OPEN`;

export type IRootProductSwitchState = Immutable<{
	productSwitch: {
		product: 'sportsbook' | 'casino';
		timestamp: number;
		isProductSwitchPopupOpen: boolean;
	};
	translations: {
		[key: string]: string;
	};
}>;

interface IProductActiveResult {
	product: 'sportsbook' | 'casino';
	timestamp: number;
	acknowledged: boolean;
}

interface IProductActive {
	status: 'SUCCESS';
	result?: {
		product: 'sportsbook' | 'casino';
		secondsLeft: number;
		acknowledged: boolean;
	};
}

export type ThunkAction<
	ReturnType = void,
	State = IRootProductSwitchState,
	ExtraThunkArg = undefined,
	BasicAction extends ReduxAction = ReduxAction
> = (
	dispatch: ThunkDispatch<State, ExtraThunkArg, BasicAction>,
	getState: () => State,
	extraArgument: ExtraThunkArg
) => ReturnType;

export type ThunkProductSwitch = ThunkAction<void, IRootProductSwitchState, undefined, ReduxAction>;
type AppAction<Actions extends ReduxAction = ReduxAnyAction> = Actions;
type AppDispatch = ThunkDispatch<IRootProductSwitchState, undefined, AppAction>;

export const useProductSwitchDispatch: () => AppDispatch = useDispatch;

// Selectors
export const selectIsPopupOpen = (state: IRootProductSwitchState) => state[REDUCER_NAME].isProductSwitchPopupOpen;
export const selectProductSwitchState = (state: IRootProductSwitchState) => {
	return {
		timestamp: state[REDUCER_NAME].timestamp,
		activeProduct: state[REDUCER_NAME].product
	};
};

export const updateModalIsOpen = (isModalOpen: boolean) => {
	return {
		type: UPDATED_MODAL_OPEN,
		payload: {
			isProductSwitchPopupOpen: isModalOpen
		}
	};
};
export const clearProductSwitch = () => {
	return {
		type: PRODUCT_COOLOFF_CLEARED
	};
};
const updateProductSwitchData = (secondsLeft: number, product?: 'sportsbook' | 'casino') => {
	return {
		type: PRODUCT_COOLOFF_UPDATED,
		payload: {
			timestamp: secondsLeft > 0 ? getFutureTimestamp(secondsLeft) : 0,
			product
		}
	};
};

export const checkProductCooloff =
	(product: string): ThunkProductSwitch =>
	async dispatch => {
		try {
			const response = await requestApi<IProductActive>({
				endpoint: `${COOLOFF_API_BASE_URL}/product/switch/${product}`
			});
			if (!response.result || !response.result.product) {
				dispatch(clearProductSwitch());
				return;
			}
			if (response.result.acknowledged && response.result.secondsLeft > 0) {
				// keep time left for timer only when user accepted product switch
				dispatch(updateProductSwitchData(response.result.secondsLeft));
				return;
			}

			dispatch(updateProductSwitchData(response.result.secondsLeft, response.result.product));
			dispatch(updateModalIsOpen(true));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
		}
	};

export const postProductSwitchEvent =
	(fromProduct: string, toProduct: string): ThunkProductSwitch =>
	async () => {
		try {
			await requestApi({
				endpoint: `${COOLOFF_API_BASE_URL}/product/switch/event/${fromProduct}/${toProduct}`,
				method: 'POST'
			});
		} catch (error) {
			// TODO Handle error
		}
	};

// Reducer
const initialState = {
	timestamp: undefined,
	product: undefined,
	isProductSwitchPopupOpen: false
};

interface IReducerPayload extends Partial<IProductActiveResult> {
	isProductSwitchPopupOpen?: boolean;
}
const reducer = (
	state = initialState,
	{ type, payload }: { type: string; payload: IReducerPayload } = { type: '', payload: {} }
) => {
	switch (type) {
		case PRODUCT_COOLOFF_CLEARED:
			return {
				...state,
				product: undefined,
				timestamp: undefined
			};
		case PRODUCT_COOLOFF_UPDATED:
			return {
				...state,
				product: payload.product,
				timestamp: payload.timestamp
			};
		case UPDATED_MODAL_OPEN:
			return {
				...state,
				isProductSwitchPopupOpen: payload.isProductSwitchPopupOpen
			};
		case LOGOUT:
			return {
				...state,
				product: undefined,
				timestamp: undefined,
				isProductSwitchPopupOpen: false
			};
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);
export default reducer;
